/**
 * Defensive readers for Vipps-sourced ID token claims issued via B2C.
 * Claims are only present if the user consented to sharing them in Vipps,
 * and the email claim may arrive as `email` (string) or `emails` (array).
 * Never fall back to `sub` or `username` — under Vipps those are not emails.
 */

/**
 * Extract the user's email from an MSAL account's ID token claims.
 * @param {Object} account - MSAL account (from getActiveAccount()/accounts[])
 * @returns {string|null} The email address, or null if no email claim exists
 */
export const getEmailFromClaims = (account) => {
  const claims = account?.idTokenClaims;
  if (!claims) return null;

  if (typeof claims.email === 'string' && claims.email.includes('@')) {
    return claims.email;
  }

  if (Array.isArray(claims.emails)) {
    const first = claims.emails.find(
      (e) => typeof e === 'string' && e.includes('@')
    );
    if (first) return first;
  }

  return null;
};

/**
 * Extract the user's first name from an MSAL account's ID token claims.
 * @param {Object} account - MSAL account (from getActiveAccount()/accounts[])
 * @returns {string|null} The given name, or null if the claim is absent
 */
export const getGivenNameFromClaims = (account) => {
  const givenName = account?.idTokenClaims?.given_name;
  if (typeof givenName === 'string' && givenName.trim().length > 0) {
    return givenName.trim();
  }
  return null;
};

/**
 * Extract the user's first name from an MSAL account's ID token claims.
 * `given_name` can hold several names ("Dan Vegar") — this returns only the
 * first whitespace-separated token. Hyphenated names ("Ann-Kristin") are one
 * token and stay intact.
 * @param {Object} account - MSAL account (from getActiveAccount()/accounts[])
 * @returns {string|null} The first name, or null if the claim is absent
 */
export const getFirstNameFromClaims = (account) => {
  const givenName = getGivenNameFromClaims(account);
  return givenName ? givenName.split(/\s+/)[0] : null;
};
